import React, { useState } from 'react';
import { FiCheck, FiX, FiPercent, FiHash } from 'react-icons/fi';
import './ProgressionEditor.css';

const ProgressionEditor = ({ sourceCycle, onApply, onCancel }) => {
  // Collect all unique exercises from source cycle
  const [exercises, setExercises] = useState(() => {
    const exerciseMap = new Map();

    // Safety check: ensure sourceCycle and sessions exist
    if (!sourceCycle || !sourceCycle.sessions) {
      return [];
    }

    sourceCycle.sessions.forEach(session => {
      if (!session || !session.blocks) return;

      session.blocks.forEach(block => {
        if (!block || !block.exercises) return;

        block.exercises.forEach(exercise => {
          if (!exercise || !exercise.exerciseId) return;

          if (!exerciseMap.has(exercise.exerciseId)) {
            exerciseMap.set(exercise.exerciseId, {
              id: exercise.exerciseId,
              name: exercise.exerciseName,
              currentWeight: exercise.primaryMetrics?.weight,
              currentSets: exercise.primaryMetrics?.sets,
              currentReps: exercise.primaryMetrics?.reps
            });
          }
        });
      });
    });

    return Array.from(exerciseMap.values());
  });

  // Default progression settings
  const [defaultSettings, setDefaultSettings] = useState({
    weightIncrease: 2.5,
    weightIncreaseType: 'absolute', // 'absolute' or 'percentage'
    setIncrease: 0,
    repIncrease: 0
  });

  // Per-exercise overrides
  const [exerciseSettings, setExerciseSettings] = useState({});

  const [useDefaultForAll, setUseDefaultForAll] = useState(true);

  const updateDefaultSetting = (field, value) => {
    setDefaultSettings(prev => ({ ...prev, [field]: value }));
  };

  const updateExerciseSetting = (exerciseId, field, value) => {
    setExerciseSettings(prev => ({
      ...prev,
      [exerciseId]: {
        ...prev[exerciseId],
        [field]: value
      }
    }));
  };

  const getExerciseSetting = (exerciseId, field) => {
    if (useDefaultForAll || !exerciseSettings[exerciseId]) {
      return defaultSettings[field];
    }
    return exerciseSettings[exerciseId][field] !== undefined
      ? exerciseSettings[exerciseId][field]
      : defaultSettings[field];
  };

  const handleApply = () => {
    const progressionSettings = {
      default: defaultSettings,
      ...exerciseSettings
    };
    onApply(progressionSettings);
  };

  const calculateNewValue = (exercise, field) => {
    const setting = getExerciseSetting(exercise.id, field);

    switch (field) {
      case 'weightIncrease':
        if (!exercise.currentWeight) return 'N/A';
        const weightIncreaseType = getExerciseSetting(exercise.id, 'weightIncreaseType');
        if (weightIncreaseType === 'percentage') {
          const increase = exercise.currentWeight * (setting / 100);
          return `${exercise.currentWeight} → ${(exercise.currentWeight + increase).toFixed(1)}kg`;
        } else {
          return `${exercise.currentWeight} → ${(exercise.currentWeight + setting).toFixed(1)}kg`;
        }

      case 'setIncrease':
        if (!exercise.currentSets || setting === 0) return 'No change';
        return `${exercise.currentSets} → ${exercise.currentSets + setting} sets`;

      case 'repIncrease':
        if (!exercise.currentReps || setting === 0) return 'No change';
        if (typeof exercise.currentReps === 'string' && exercise.currentReps.includes('-')) {
          const [min, max] = exercise.currentReps.split('-').map(Number);
          return `${exercise.currentReps} → ${min + setting}-${max + setting}`;
        } else {
          const current = parseInt(exercise.currentReps);
          return `${exercise.currentReps} → ${current + setting}`;
        }

      default:
        return '';
    }
  };

  // If no exercises found, show error
  if (exercises.length === 0) {
    return (
      <div className="progression-editor">
        <div className="progression-header">
          <h3>No Exercises Found</h3>
          <p>The source cycle doesn't contain any exercises. Please add exercises first.</p>
        </div>
        <div className="progression-actions">
          <button className="cancel-btn" onClick={onCancel}>
            <FiX /> Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="progression-editor">
      <div className="progression-header">
        <h3>Configure Linear Progression</h3>
        <p>Set how exercises should progress from the previous cycle</p>
      </div>

      {/* Default Settings */}
      <div className="default-settings-section">
        <div className="section-header">
          <h4>Default Progression (applies to all exercises)</h4>
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={useDefaultForAll}
              onChange={(e) => setUseDefaultForAll(e.target.checked)}
            />
            Use same settings for all exercises
          </label>
        </div>

        <div className="settings-grid">
          <div className="setting-group">
            <label>Weight Increase</label>
            <div className="input-with-toggle">
              <input
                type="number"
                step="0.5"
                min="0"
                value={defaultSettings.weightIncrease}
                onChange={(e) => updateDefaultSetting('weightIncrease', parseFloat(e.target.value))}
              />
              <div className="toggle-buttons">
                <button
                  className={defaultSettings.weightIncreaseType === 'absolute' ? 'active' : ''}
                  onClick={() => updateDefaultSetting('weightIncreaseType', 'absolute')}
                  title="Absolute increase (kg)"
                >
                  <FiHash /> kg
                </button>
                <button
                  className={defaultSettings.weightIncreaseType === 'percentage' ? 'active' : ''}
                  onClick={() => updateDefaultSetting('weightIncreaseType', 'percentage')}
                  title="Percentage increase"
                >
                  <FiPercent /> %
                </button>
              </div>
            </div>
          </div>

          <div className="setting-group">
            <label>Set Increase</label>
            <input
              type="number"
              min="0"
              max="3"
              value={defaultSettings.setIncrease}
              onChange={(e) => updateDefaultSetting('setIncrease', parseInt(e.target.value))}
            />
          </div>

          <div className="setting-group">
            <label>Rep Increase</label>
            <input
              type="number"
              min="0"
              max="5"
              value={defaultSettings.repIncrease}
              onChange={(e) => updateDefaultSetting('repIncrease', parseInt(e.target.value))}
            />
          </div>
        </div>
      </div>

      {/* Per-Exercise Settings */}
      {!useDefaultForAll && (
        <div className="per-exercise-section">
          <h4>Per-Exercise Customization</h4>
          <div className="exercises-table">
            <div className="table-header">
              <div>Exercise</div>
              <div>Current</div>
              <div>Weight Increase</div>
              <div>Type</div>
              <div>Sets</div>
              <div>Reps</div>
              <div>Preview</div>
            </div>
            {exercises.map(exercise => (
              <div key={exercise.id} className="table-row">
                <div className="exercise-name">{exercise.name}</div>
                <div className="current-values">
                  {exercise.currentWeight ? `${exercise.currentWeight}kg` : 'BW'} ×{' '}
                  {exercise.currentSets} × {exercise.currentReps}
                </div>
                <div>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={getExerciseSetting(exercise.id, 'weightIncrease')}
                    onChange={(e) => updateExerciseSetting(exercise.id, 'weightIncrease', parseFloat(e.target.value))}
                  />
                </div>
                <div>
                  <select
                    value={getExerciseSetting(exercise.id, 'weightIncreaseType')}
                    onChange={(e) => updateExerciseSetting(exercise.id, 'weightIncreaseType', e.target.value)}
                  >
                    <option value="absolute">kg</option>
                    <option value="percentage">%</option>
                  </select>
                </div>
                <div>
                  <input
                    type="number"
                    min="0"
                    max="3"
                    value={getExerciseSetting(exercise.id, 'setIncrease')}
                    onChange={(e) => updateExerciseSetting(exercise.id, 'setIncrease', parseInt(e.target.value))}
                  />
                </div>
                <div>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    value={getExerciseSetting(exercise.id, 'repIncrease')}
                    onChange={(e) => updateExerciseSetting(exercise.id, 'repIncrease', parseInt(e.target.value))}
                  />
                </div>
                <div className="preview">
                  {calculateNewValue(exercise, 'weightIncrease')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Preview Summary */}
      {useDefaultForAll && (
        <div className="preview-summary">
          <h4>Preview Changes</h4>
          <div className="preview-list">
            {exercises.slice(0, 5).map(exercise => (
              <div key={exercise.id} className="preview-item">
                <span className="exercise-name">{exercise.name}</span>
                <span className="preview-change">{calculateNewValue(exercise, 'weightIncrease')}</span>
              </div>
            ))}
            {exercises.length > 5 && (
              <div className="preview-more">
                ...and {exercises.length - 5} more exercises
              </div>
            )}
          </div>
        </div>
      )}

      <div className="progression-actions">
        <button className="cancel-btn" onClick={onCancel}>
          <FiX /> Cancel
        </button>
        <button className="apply-btn" onClick={handleApply}>
          <FiCheck /> Apply Progression
        </button>
      </div>
    </div>
  );
};

export default ProgressionEditor;
